import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getBudget, updateItemAllocations } from '../../../models/budgetsModel';

function BudgetItemAllocation({ isOpen, onClose, budgetId, budgetAmount, monthName, onSuccess }) {
  const [items, setItems] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  useEffect(() => {
    if (isOpen && budgetId) {
      loadBudgetItems();
    }
  }, [isOpen, budgetId]);

  const loadBudgetItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBudget(budgetId);
      const responseData = response?.data || response || {};
      const budgetItems = responseData.items || [];
      
      const transformedItems = budgetItems.map(item => ({
        budget_month_id: item.budget_month_id,
        budget_item_id: item.budget_item_id || item.budget_item,
        budget_item_name: item.budget_item_name,
        budget_item_gl: item.budget_item_gl,
        budget_amount: item.budget_amount || 0
      }));
      
      setItems(transformedItems);
      
      const initialAllocations = {};
      transformedItems.forEach(item => {
        if (item.budget_month_id) {
          initialAllocations[item.budget_month_id] = parseFloat(item.budget_amount) || 0;
        }
      });
      setAllocations(initialAllocations);
    } catch (err) {
      console.error('Error loading budget items:', err);
      setError('Failed to load budget items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocationChange = (budgetMonthId, value) => {
    const numValue = parseFloat(value) || 0;
    setAllocations(prev => ({
      ...prev,
      [budgetMonthId]: numValue
    }));
  };

  const getTotalAllocated = () => {
    return Object.values(allocations).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const getRemainingAmount = () => {
    return (parseFloat(budgetAmount) || 0) - getTotalAllocated();
  };

  const distributeEvenly = () => {
    if (items.length === 0) return;
    const totalBudget = parseFloat(budgetAmount) || 0;
    const perItem = Math.floor(totalBudget / items.length);
    const remainder = totalBudget - (perItem * items.length);
    
    const newAllocations = {};
    items.forEach((item, index) => {
      newAllocations[item.budget_month_id] = index === 0 ? perItem + remainder : perItem;
    });
    setAllocations(newAllocations);
  };

  const clearAllocations = () => {
    const clearedAllocations = {};
    items.forEach(item => {
      clearedAllocations[item.budget_month_id] = 0;
    });
    setAllocations(clearedAllocations);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await updateItemAllocations(budgetId, allocations);
      toast.success('Budget allocations saved successfully!');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error saving allocations:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save allocations. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const totalAllocated = getTotalAllocated();
  const remaining = getRemainingAmount();
  const isOverBudget = remaining < 0;
  const isFullyAllocated = Math.abs(remaining) < 0.01;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          {/* Header */}
          <div className="flex justify-between items-start p-4 border-b">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Allocate Budget to Items</h3>
              <p className="text-sm text-gray-500">Split the total budget amount across individual items</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Budget Summary */}
          <div className="bg-gray-50 px-4 py-3 border-b">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Total Budget {monthName && `for ${monthName}`}</p>
                <p className="text-base font-bold text-gray-900">{formatCurrency(budgetAmount)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Allocated</p>
                <p className={`text-base font-bold ${isOverBudget ? 'text-red-600' : 'text-blue-600'}`}>
                  {formatCurrency(totalAllocated)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Unallocated</p>
                <p className={`text-base font-bold ${isOverBudget ? 'text-red-600' : isFullyAllocated ? 'text-green-600' : 'text-yellow-600'}`}>
                  {formatCurrency(remaining)}
                </p>
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-3 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={distributeEvenly}
                className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded"
              >
                Distribute Evenly
              </button>
              <button
                type="button"
                onClick={clearAllocations}
                className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="m-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No items found for this budget. Add items first using the Edit button.
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase w-36">Amount</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((item) => {
                      const allocation = allocations[item.budget_month_id] || 0;
                      const percentage = budgetAmount > 0 ? ((allocation / budgetAmount) * 100).toFixed(1) : 0;
                      
                      return (
                        <tr key={item.budget_month_id} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <div className="text-sm font-medium text-gray-900">{item.budget_item_name}</div>
                            {item.budget_item_gl && (
                              <div className="text-xs text-gray-500">GL: {item.budget_item_gl}</div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center">
                              <span className="text-gray-500 text-sm mr-1">R</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={allocation}
                                onChange={(e) => handleAllocationChange(item.budget_month_id, e.target.value)}
                                className="w-full px-2 py-1 text-right border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right text-sm text-gray-600">
                            {percentage}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {isOverBudget && (
              <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Allocations exceed budget by {formatCurrency(Math.abs(remaining))}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || items.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Allocations'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BudgetItemAllocation;
